/**
 * 統一緩存服務
 * 為所有 API 服務提供一致的緩存機制
 */

import { logger } from './logger';

export interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  maxSize?: number; // Maximum cache size
}

export class CacheService<T = any> {
  private cache = new Map<string, CacheItem<T>>();
  private readonly defaultTTL: number;
  private readonly maxSize: number;

  constructor(options: CacheOptions = {}) {
    this.defaultTTL = options.ttl || 5 * 60 * 1000; // 5 minutes default
    this.maxSize = options.maxSize || 100;
  }

  /**
   * 設置緩存項目
   */
  set(key: string, data: T, ttl?: number): void {
    const now = Date.now();
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: now,
      expiry: now + (ttl || this.defaultTTL)
    };

    this.cache.set(key, cacheItem);
    this.cleanup();
  }

  /**
   * 獲取緩存項目
   */
  get(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now > item.expiry) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  /**
   * 檢查緩存是否存在且有效
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    const now = Date.now();
    if (now > item.expiry) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * 刪除指定緩存
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * 清空所有緩存
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * 獲取緩存大小
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * 獲取緩存統計信息
   */
  getStats() {
    const now = Date.now();
    let validCount = 0;
    let expiredCount = 0;

    const entries = Array.from(this.cache.entries());
    for (const [, item] of entries) {
      if (now > item.expiry) {
        expiredCount++;
      } else {
        validCount++;
      }
    }

    return {
      totalItems: this.cache.size,
      validItems: validCount,
      expiredItems: expiredCount,
      maxSize: this.maxSize,
      defaultTTL: this.defaultTTL
    };
  }

  /**
   * 清理過期項目和超過大小限制的項目
   */
  private cleanup(): void {
    const now = Date.now();
    
    // 清理過期項目
    const entries = Array.from(this.cache.entries());
    for (const [key, item] of entries) {
      if (now > item.expiry) {
        this.cache.delete(key);
      }
    }

    // 如果超過大小限制，刪除最舊的項目
    if (this.cache.size > this.maxSize) {
      const entries = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const itemsToDelete = entries.slice(0, this.cache.size - this.maxSize);
      itemsToDelete.forEach(([key]) => this.cache.delete(key));
    }
  }
}

/**
 * 緩存管理器 - 管理多個命名緩存
 */
export class CacheManager {
  private static instance: CacheManager;
  private caches = new Map<string, CacheService>();

  private constructor() {}

  static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  /**
   * 獲取或創建命名緩存
   */
  getCache<T>(name: string, options?: CacheOptions): CacheService<T> {
    if (!this.caches.has(name)) {
      this.caches.set(name, new CacheService<T>(options));
      logger.service.info(`創建緩存實例: ${name}`, options);
    }
    return this.caches.get(name) as CacheService<T>;
  }

  /**
   * 清空所有緩存
   */
  clearAll(): void {
    const caches = Array.from(this.caches.values());
    for (const cache of caches) {
      cache.clear();
    }
    logger.service.info('清空所有緩存');
  }

  /**
   * 獲取所有緩存的統計信息
   */
  getAllStats() {
    const stats: Record<string, any> = {};
    const entries = Array.from(this.caches.entries());
    for (const [name, cache] of entries) {
      stats[name] = cache.getStats();
    }
    return stats;
  }

  /**
   * 刪除命名緩存
   */
  deleteCache(name: string): boolean {
    const cache = this.caches.get(name);
    if (cache) {
      cache.clear();
      this.caches.delete(name);
      logger.service.info(`刪除緩存實例: ${name}`);
      return true;
    }
    return false;
  }
}

// 預定義的緩存實例
export const cacheManager = CacheManager.getInstance();

// 常用緩存實例
export const stockPriceCache = cacheManager.getCache('stockPrices', { 
  ttl: 60 * 1000, // 1 minute for real-time data
  maxSize: 1000 
});

export const stockDataCache = cacheManager.getCache('stockData', { 
  ttl: 5 * 60 * 1000, // 5 minutes for general stock data
  maxSize: 500 
});

export const marketDataCache = cacheManager.getCache('marketData', { 
  ttl: 60 * 1000, // 1 minute for market indices
  maxSize: 100 
});

export const realtimeCache = cacheManager.getCache('realtime', { 
  ttl: 30 * 1000, // 30 seconds for real-time data
  maxSize: 200 
});
